/* eslint-disable react/jsx-props-no-spreading */
import { FC, useCallback, useEffect, useState } from 'react';
import NumberFormat from 'react-number-format';
import { useTranslate } from 'hooks/useTranslate';

import { Partner, Society } from 'types';
import {
  currencyFormatDecimals,
  numberFormat,
  percentFormatDecimals,
} from 'constants/formats';
import { getPartnerAmountFormContributions } from 'utils/getPartnerAmountFromContributions';
import { getPartnerDetailsConfigByCorporateId } from 'modules/partners/utils/getPartnerDetailsConfigByCorporateId';
import IconTooltip from 'modules/_shared/components/Icon/IconTooltip';
import { formatNumber } from 'utils/formats';
import { hasShowDetails } from 'utils/showDetails';

type PartnerSocietyMetricsProps = {
  fullName: string;
  society: Society;
  partner: Partner;
  inversion: number;
  NDpercent: number;
  decimalScale: number;
  isAdmin: boolean;
  isLoading: boolean;
};

const PartnerSocietyMetrics: FC<PartnerSocietyMetricsProps> = ({
  fullName,
  society,
  partner,
  inversion,
  decimalScale,
  NDpercent,
  isAdmin,
  isLoading,
}) => {
  const { t } = useTranslate();

  const [totalInvested, setTotalInvested] = useState<number>(0);
  const {
    showTotalShares,
    showTotalCoOwnerShares,
    showPercentageShares,
    showTotalInvested,
  } = getPartnerDetailsConfigByCorporateId(society);

  const calculateTotalInvested = useCallback(async () => {
    if (!partner || !society) return 0;

    try {
      const partnerAmount: number = inversion;
      const partnerContributions: number =
        await getPartnerAmountFormContributions(partner, society);

      const invested = partnerAmount + partnerContributions;
      setTotalInvested(invested);
    } catch {
      return 0;
    }
  }, [partner, society, inversion]);

  useEffect(() => {
    calculateTotalInvested();
  }, [calculateTotalInvested]);

  const sharesBurdenCount = partner?.sharesBurdenCount;
  const sharesBurdenTooltip = [
    `${t('CanjeMovementsHolder')}: ${formatNumber(
      sharesBurdenCount?.coOwnerPrimary || 0
    )}`,
    `${t('DEFAULT_CATEGORY_CO_OWNERSHIP')}: ${formatNumber(
      sharesBurdenCount?.coOwnerSecondary || 0
    )}`,
    `${t('ShareEncumbranceOption_Usufruct')}: ${formatNumber(
      sharesBurdenCount?.usufruct || 0
    )}`,
    `${t('Others')}: ${formatNumber(sharesBurdenCount?.otherBurdens || 0)}`,
  ].join('\n');

  return (
    <div className="card-inner">
      <h6 className="overline-title-alt mb-2">
        {t('InvestmentOfFullnameInSoc', {
          fullName,
          societyName: society.name,
        })}
      </h6>

      <div className="d-flex justify-content-around flex-wrap text-center">
        {(showTotalShares || isAdmin) && (
          <div className="my-2">
            <div className="profile-balance-amount">
              <div className="number">
                <NumberFormat
                  value={
                    (partner?.sharesCount?.actual || 0) +
                    (partner?.sharesBurdenCount?.coOwnerSecondary || 0)
                  }
                  displayType="text"
                  {...numberFormat}
                />
              </div>
            </div>
            <div className="profile-stats mw-120px">
              <span className="text-primary">
                {showTotalCoOwnerShares ? t('SharesAsOwner') : t('Shares')}
              </span>
            </div>
          </div>
        )}

        {(showTotalCoOwnerShares || isAdmin) && (
          <div className="my-2">
            <div className="profile-balance-amount">
              <div className="number">
                <NumberFormat
                  value={
                    (partner?.sharesBurdenCount?.coOwnerPrimary || 0) +
                    (partner?.sharesBurdenCount?.coOwnerSecondary || 0)
                  }
                  displayType="text"
                  {...numberFormat}
                />
              </div>
            </div>
            <div className="profile-stats mw-120px">
              <span className="text-primary">{t('SharesWithBurden')}</span>
              {hasShowDetails() && (
                <IconTooltip
                  id="partner-details-shares-burden-tooltip"
                  tooltip={sharesBurdenTooltip}
                  place="bottom"
                />
              )}
            </div>
          </div>
        )}

        {(showPercentageShares || isAdmin) && (
          <div className="my-2">
            <div className="profile-balance-amount">
              <div className="number">
                <NumberFormat
                  value={NDpercent || 0}
                  displayType="text"
                  {...percentFormatDecimals}
                  decimalScale={decimalScale}
                />
              </div>
            </div>
            <div className="profile-stats mw-120px">
              <span className="text-primary">{t('Percentage')}</span>
              <IconTooltip
                id="partner-details-percentage-tooltip"
                tooltip={t('PartnerDetailsPercentageTooltip')}
                place="bottom"
                className="text-left"
              />
            </div>
          </div>
        )}

        {showTotalInvested && (
          <div className="my-2">
            <div className="profile-balance-amount">
              <div className="number">
                {isLoading ? (
                  <p className="placeholder-glow ">
                    <span className="placeholder offset-2 h-20px" />
                    <span className="placeholder col-8    h-20px" />
                    <span className="placeholder offset-2 h-20px" />
                  </p>
                ) : (
                  <NumberFormat
                    value={totalInvested || 0}
                    displayType="text"
                    {...currencyFormatDecimals}
                  />
                )}
              </div>
            </div>
            <div className="profile-stats mw-120px">
              <span className="text-primary">{t('Invested')}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PartnerSocietyMetrics;
